import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  InputBase,
  Paper,
  Popper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Typography,
  Divider,
  CircularProgress,
  Fade,
  alpha,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import WorkIcon from '@mui/icons-material/Work';
import DescriptionIcon from '@mui/icons-material/Description';
import BusinessIcon from '@mui/icons-material/Business';
import StoreIcon from '@mui/icons-material/Store';
import NotificationsIcon from '@mui/icons-material/Notifications';
import HistoryIcon from '@mui/icons-material/History';
import PeopleIcon from '@mui/icons-material/People';
import WarningIcon from '@mui/icons-material/Warning';
import ArticleIcon from '@mui/icons-material/Article';
import { searchAll } from '../services/searchService';

const RECENT_KEY = 'ats_recent_searches';
const MAX_RECENT = 5;
const DEBOUNCE_MS = 300;
const PREVIEW_PER_TYPE = 5;

const TYPE_ICONS = {
  CANDIDATE: <PersonIcon fontSize="small" />,
  JOB: <WorkIcon fontSize="small" />,
  RESUME: <DescriptionIcon fontSize="small" />,
  COMPANY: <BusinessIcon fontSize="small" />,
  VENDOR: <StoreIcon fontSize="small" />,
  NOTIFICATION: <NotificationsIcon fontSize="small" />,
  ACTIVITY: <HistoryIcon fontSize="small" />,
  USER: <PeopleIcon fontSize="small" />,
  INCIDENT: <WarningIcon fontSize="small" />,
  AUDIT: <ArticleIcon fontSize="small" />,
};

// PR-UX-014 — this dropdown used to derive its group headings algorithmically
// (`type.charAt(0) + type.slice(1).toLowerCase() + 's'`), which silently produced wrong labels
// for any category whose plural isn't "add an s": COMPANY -> "Companys", ACTIVITY -> "Activitys",
// BENCH -> "Benchs" (all real, pre-existing bugs — not introduced by PR-UX-014's 7 new backend
// categories, just never noticed since this dropdown's heading text is easy to miss). An explicit
// table mirrors GlobalSearchResults.js's own TYPE_META labels exactly, so both search surfaces
// always agree — the algorithmic expression survives only as a fallback for a category neither
// table knows about yet, matching TYPE_ICONS' own existing fallback-to-generic-icon convention.
const TYPE_LABELS = {
  CANDIDATE: 'Candidates', JOB: 'Jobs', RESUME: 'Resumes', COMPANY: 'Companies',
  VENDOR: 'Vendors', NOTIFICATION: 'Notifications', ACTIVITY: 'Activity',
  SUBMISSION: 'Submissions', PLACEMENT: 'Placements', CONSULTANT: 'Consultants',
  BENCH: 'Bench', COMMUNICATION: 'Communications', CALENDAR: 'Calendar',
  KNOWLEDGE: 'Knowledge Base', REPORT: 'Reports',
  USER: 'Users', INCIDENT: 'Incidents', AUDIT: 'Audit Logs',
};
const typeLabel = (type) => TYPE_LABELS[type] || (type.charAt(0) + type.slice(1).toLowerCase() + 's');

function getRecentSearches() {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
  } catch {
    return [];
  }
}

function saveRecentSearch(q) {
  if (!q || q.trim().length < 2) return;
  const recent = getRecentSearches().filter(s => s !== q.trim());
  recent.unshift(q.trim());
  localStorage.setItem(RECENT_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)));
}

const GlobalSearchBar = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const anchorRef = useRef(null);
  const debounceRef = useRef(null);

  const flatResults = results
    ? Object.entries(results.grouped || {}).flatMap(([type, items]) =>
        items.slice(0, PREVIEW_PER_TYPE).map(item => ({ ...item, type }))
      )
    : [];

  // RC18.2 — Ctrl+K is now owned app-wide by the Command Palette (navigation); this field
  // stays a click/tab-to-focus entity search, matching the "search vs. navigate" split
  // documented in `components/design-system/CommandPalette.js`.
  const doSearch = useCallback(async (q) => {
    if (q.trim().length < 2) {
      setResults(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await searchAll(q.trim());
      setResults(data);
    } catch {
      setResults(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    setFocusedIndex(-1);
    clearTimeout(debounceRef.current);
    if (val.trim().length < 2) {
      setResults(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(() => doSearch(val), DEBOUNCE_MS);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex(prev => Math.min(prev + 1, flatResults.length - 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIndex(prev => Math.max(prev - 1, -1));
      return;
    }
    if (e.key === 'Enter') {
      if (focusedIndex >= 0 && flatResults[focusedIndex]) {
        navigateTo(flatResults[focusedIndex]);
      } else if (query.trim().length >= 2) {
        goToFullResults();
      }
    }
  };

  const navigateTo = (item) => {
    saveRecentSearch(query);
    setOpen(false);
    setQuery('');
    setResults(null);
    navigate(item.route);
  };

  const goToFullResults = () => {
    if (query.trim().length < 2) return;
    saveRecentSearch(query);
    setOpen(false);
    navigate(`/search?q=${encodeURIComponent(query.trim())}`);
    setQuery('');
    setResults(null);
  };

  const handleFocus = () => setOpen(true);

  const handleBlur = (e) => {
    // Delay so clicks inside the popper still register
    setTimeout(() => setOpen(false), 150);
  };

  const recent = getRecentSearches();
  const showRecent = open && query.length === 0 && recent.length > 0;
  const showResults = open && (loading || (results && flatResults.length > 0));
  const popperOpen = showRecent || showResults || (open && query.trim().length >= 2 && !loading && flatResults.length === 0);

  return (
    <Box ref={anchorRef} sx={{ position: 'relative', mx: 2 }}>
      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: { xs: 180, sm: 280, md: 360 },
          px: 1.5,
          py: 0.5,
          borderRadius: 2,
          bgcolor: theme => alpha(theme.palette.common.white, 0.15),
          '&:hover': { bgcolor: theme => alpha(theme.palette.common.white, 0.25) },
          border: 'none',
        }}
      >
        <SearchIcon sx={{ color: 'rgba(255,255,255,0.7)', mr: 1, fontSize: 20 }} />
        <InputBase
          inputRef={inputRef}
          value={query}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder="Search ATS…"
          sx={{ color: 'white', flex: 1, '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.7)' } }}
          inputProps={{ 'aria-label': 'global search' }}
        />
        {loading && <CircularProgress size={16} sx={{ color: 'rgba(255,255,255,0.7)' }} />}
      </Paper>

      <Popper
        open={popperOpen}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        transition
        style={{ zIndex: 1400, width: anchorRef.current?.offsetWidth || 360 }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={150}>
            <Paper elevation={8} sx={{ mt: 0.5, maxHeight: 480, overflowY: 'auto', borderRadius: 2 }}>

              {/* Recent searches */}
              {showRecent && (
                <>
                  <Typography variant="caption" sx={{ px: 2, pt: 1.5, pb: 0.5, display: 'block', color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Recent Searches
                  </Typography>
                  <List dense disablePadding>
                    {recent.map((r) => (
                      <ListItem
                        key={r}
                        button
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setQuery(r);
                          doSearch(r);
                        }}
                        sx={{ px: 2, py: 0.75 }}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}><HistoryIcon fontSize="small" sx={{ color: 'text.secondary' }} /></ListItemIcon>
                        <ListItemText primary={r} primaryTypographyProps={{ variant: 'body2' }} />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}

              {/* Live results */}
              {showResults && !loading && flatResults.length > 0 && (
                <>
                  {Object.entries(results.grouped || {}).map(([type, items], gi) => (
                    <React.Fragment key={type}>
                      {gi > 0 && <Divider />}
                      <Typography variant="caption" sx={{ px: 2, pt: 1.5, pb: 0.5, display: 'block', color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        {typeLabel(type)}
                      </Typography>
                      <List dense disablePadding>
                        {items.slice(0, PREVIEW_PER_TYPE).map((item) => {
                          const globalIdx = flatResults.findIndex(f => f.entityId === item.entityId && f.type === type);
                          return (
                            <ListItem
                              key={`${type}-${item.entityId}`}
                              button
                              selected={focusedIndex === globalIdx}
                              onMouseDown={(e) => { e.preventDefault(); navigateTo(item); }}
                              sx={{ px: 2, py: 0.75, '&.Mui-selected': { bgcolor: 'action.selected' } }}
                            >
                              <ListItemIcon sx={{ minWidth: 32 }}>{TYPE_ICONS[type] || <SearchIcon fontSize="small" />}</ListItemIcon>
                              <ListItemText
                                primary={item.title}
                                secondary={item.subtitle}
                                primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                                secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                              />
                            </ListItem>
                          );
                        })}
                      </List>
                    </React.Fragment>
                  ))}
                  {results.totalResults > PREVIEW_PER_TYPE && (
                    <>
                      <Divider />
                      <ListItem
                        button
                        onMouseDown={(e) => { e.preventDefault(); goToFullResults(); }}
                        sx={{ px: 2, py: 1, justifyContent: 'center' }}
                      >
                        <Typography variant="body2" color="primary">
                          View all {results.totalResults} results for "{query}"
                        </Typography>
                      </ListItem>
                    </>
                  )}
                </>
              )}

              {/* No results */}
              {open && !loading && query.trim().length >= 2 && results && flatResults.length === 0 && (
                <Box sx={{ px: 2, py: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">No results for "{query}"</Typography>
                </Box>
              )}

            </Paper>
          </Fade>
        )}
      </Popper>
    </Box>
  );
};

export default GlobalSearchBar;
